import EditIcon from '@mui/icons-material/Edit';
import { makeStyles } from '@mui/styles';
import { formatDate } from '@telerik/kendo-intl';
import {
  PageCard,
  PageCardRow,
  PageCardSection,
  Render,
} from 'Components/Display';
import { TextIconButton } from 'Components/Form';
import { SecureComponent } from 'Components/Security';
import {
  ModalFormProvider,
  useEntity,
  useGlobalContext,
  useToasts,
} from 'Context';
import { Permission } from 'Models/Templates/Permission/Permission';
import {
  addEditRoute,
  AddEditSubRoute,
  ClaimantsSubRoute,
  ClaimsSubRoute,
  EntitiesSubRoute,
  entitiesSubRouteName,
  ReferralSubRoute,
  ROUTES,
} from 'Navigation';
import React, { Fragment, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem, Col, Row } from 'reactstrap';
import { buildRoute } from 'Utilities';
import { parseRawDateString, parseUtcToLocal } from 'Utilities/DateTimeUtc';
import ReadOnlyField from 'v3/Components/Form/ReadOnlyField';
import { TitleReadOnly } from 'v3/Components/Form/TitleReadOnly';
import { createReferralRequestSchema } from 'v3/Components/Pages/AddEditReferralsRequest/useReferralRequestForm';
import { LineOfBusinessEnum } from 'v3/Models/Enums';
import { CloseReferralDialog } from '.';
enum RoleSelector {
  DoctorAdmin = 'Doctor Admin',
  DoctorAdministrator = 'Doctor Administrator',
  StaffUser = 'Staff User',
  AccountManager = 'Accounts Manager',
  MedicalDirector = 'Medical Director',
}
export const ViewReferralBreadcrumbs: React.FC = () => {
  const entity = useEntity();
  const toast = useToasts();

  let claimantEndpoint = buildRoute(
    ROUTES.CLAIMANT,
    `${entity.claimantId}`,
    ClaimantsSubRoute.View
  );
  let claimEndpoint = buildRoute(
    ROUTES.CLAIM,
    `${entity.claimId}`,
    ClaimsSubRoute.View
  );
  let referralEndpoint = buildRoute(
    ROUTES.REFERRAL,
    `${entity.referralId}`,
    ReferralSubRoute.View
  );

  const handleSuccess = () => {
    toast.success(`Case has been marked as closed`);
  };
  const lob = entity.lineOfBusiness?.value;

  return (
    <Fragment>
      <Breadcrumb tag="nav" listTag="div" listClassName="d-flex">
        <BreadcrumbItem className="line-height-largest">
          <Link to={entitiesSubRouteName(EntitiesSubRoute.Claimants)}>
            Claimants
          </Link>
        </BreadcrumbItem>
        <BreadcrumbItem className="line-height-largest">
          <Link to={claimantEndpoint}>{entity.claimantName}</Link>
        </BreadcrumbItem>
        <BreadcrumbItem className="line-height-largest">
          <Link to={claimEndpoint}>{entity.claimNumber}</Link>
        </BreadcrumbItem>
        <BreadcrumbItem className="flex-grow-1 line-height-largest">
          <Link to={referralEndpoint}>{entity.name}</Link>
          <ModalFormProvider>
            <CloseReferralDialog />
          </ModalFormProvider>
        </BreadcrumbItem>
      </Breadcrumb>
    </Fragment>
  );
};

export const ViewReferral: React.FC = () => {
  const entity = useEntity();
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;

  const useStyles = makeStyles({
    root: {
      minWidth: 275,
    },
    title: {
      fontSize: 14,
    },
    pos: {
      marginBottom: 12,
    },
    overFlow: {
      overflowWrap: 'break-word',
    },
  });

  const history = useHistory();
  const editClaimant = () => {
    history.push(addEditRoute(AddEditSubRoute.Claimant, entity.claimantId));
  };

  const editClaim = () => {
    history.push(addEditRoute(AddEditSubRoute.Claims, entity.claimId));
  };
  const lob = entity.lineOfBusiness?.value;
  const ReferralRequestSchema = useMemo(
    () => createReferralRequestSchema(Number(lob) as LineOfBusinessEnum),
    [lob]
  );

  const classes = useStyles();
  const userId = userdata?.id;

  let hasExternalAdjuster =
    entity.externalAdjuster != null && entity.externalAdjuster != ''
      ? true
      : false;

  let showSection = useMemo(() => {
    const isDoctorAdmin = userdata?.isDoctorAdmin;
    const isInternal = userdata?.isInternal;
    const isMedicalDirector = userdata?.isMedicalDirector;

    return (
      userdata?.isAdmin ||
      userdata?.isReviewer ||
      isDoctorAdmin ||
      userdata?.isDoctor ||
      isMedicalDirector ||
      isInternal /* Staff user and account manager */
    );
  }, [userdata]);

  return (
    <div className="read-only-content">
      {showSection && (
        <Fragment>
          <PageCard className={classes.root}>
            <PageCardRow>
              <PageCardSection title="Information">
                <Row>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">Claimant:</span>
                    <span className="field-value">
                      <Link
                        to={buildRoute(
                          ROUTES.CLAIMANT,
                          entity.claimantId,
                          ClaimantsSubRoute.View
                        )}
                      >
                        {entity.claimantName}
                      </Link>
                      <SecureComponent permissions={[Permission.EditClaimant]}>
                        <TextIconButton
                          icon={EditIcon}
                          className="d-inline ml-3"
                          onClick={editClaimant}
                        />
                      </SecureComponent>
                    </span>
                  </Col>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">Claim:</span>
                    <span className="field-value">
                      <Link
                        to={buildRoute(
                          ROUTES.CLAIM,
                          entity.claimId,
                          ClaimsSubRoute.View
                        )}
                      >
                        {entity.claimNumber} ({entity.clientName})
                      </Link>
                      <SecureComponent permissions={[Permission.EditClaim]}>
                        <TextIconButton
                          icon={EditIcon}
                          className="d-inline ml-3"
                          onClick={editClaim}
                        />
                      </SecureComponent>
                    </span>
                  </Col>
                </Row>
                <Row>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">
                      Referrer Name:
                    </span>
                    <span className="field-value">{entity.name}</span>
                  </Col>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">
                      Provider Phone:
                    </span>
                    <span className="field-value">{entity.providerPhone}</span>
                  </Col>
                </Row>
                <Row>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">
                      Provider Requesting Treatment:
                    </span>
                    <span className="field-value">
                      {entity.providerRequestingTreatment}
                    </span>
                  </Col>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">
                      Referral Date:
                    </span>
                    <span className="field-value">
                      {entity.referralDate
                        ? formatDate(parseUtcToLocal(entity.referralDate) || new Date(), 'd')
                        : ''}
                    </span>
                  </Col>
                </Row>
                <Row>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">
                      Referral Number:
                    </span>
                    <span className="field-value">{entity.referralNumber}</span>
                  </Col>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">
                      Requested Reviewer Specialty:
                    </span>
                    <span className="field-value">
                      {entity.reviewerSpeciality}
                    </span>
                  </Col>
                </Row>
                <Row className={classes.overFlow}>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">
                      Referral Type:
                    </span>
                    <span className="field-value">
                      {entity.lineOfBusiness?.displayName}
                    </span>
                  </Col>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">
                      Special Instructions:
                    </span>
                    <span className="field-value"  style={{ lineHeight: '25px', verticalAlign: 'bottom' }}>
                      {entity.specialInstructions}
                    </span>
                  </Col>
                </Row>
                <Render condition={!userdata?.isReviewer}>
                  <Row>
                    <Col xs="12" lg="6">
                      <span className="field-label data-display">
                        Due Date & Time:
                      </span>
                      <span className="field-value">
                        {entity.dueDate
                          ? formatDate(parseUtcToLocal(entity.dueDate.toString()) || new Date(), 'g')
                          : ''}
                      </span>
                    </Col>
                  </Row>
                </Render>
                <Row>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">
                      Type of Evaluation
                    </span>
                    <span className="field-value">
                      {entity?.subService?.name}
                    </span>
                  </Col>
                  <Col xs="12" lg="6">
                    <span className="field-label data-display">IME Status</span>
                    <span className="field-value">{entity.imeStatus}</span>
                  </Col>
                </Row>
                <Row>
                  {hasExternalAdjuster && (
                    <Col xs="12" lg="6">
                      <span className="field-label data-display">
                        External Adjuster:
                      </span>
                      <span className="field-value">
                        {entity.externalAdjuster}
                      </span>
                    </Col>
                  )}
                  {!hasExternalAdjuster && (
                    <Col xs="12" lg="6">
                      <span className="field-label data-display">
                        Adjuster:
                      </span>
                      <span className="field-value">{entity.adjusterName}</span>
                    </Col>
                  )}
                </Row>
              </PageCardSection>
            </PageCardRow>
          </PageCard>
          <PageCard className={classes.root}>
            <PageCardRow>
              <PageCardSection title="General Information">
                <ReadOnlyField
                  id="clientName"
                  forceLabel="Client"
                  value={entity.clientName}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="lineOfBusiness"
                  forceLabel="Line of Business"
                  value={entity?.lineOfBusiness?.displayName}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="referralDate"
                  forceLabel="Referral Date"
                  value={
                    entity.referralDate
                      ? formatDate(parseUtcToLocal(entity.referralDate) || new Date(), 'd')
                      : ''
                  }
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="name"
                  forceLabel="Referrer"
                  value={entity.name}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="proxiedBy"
                  forceLabel="Proxied By"
                  value={entity?.proxiedBy}
                  schema={ReferralRequestSchema}
                />
              </PageCardSection>
            </PageCardRow>
          </PageCard>
        </Fragment>
      )}
      <PageCard className={classes.root} backgroundIcon="assignment_ind">
        <PageCardRow>
          <PageCardSection title="Claimant Information">
            <ReadOnlyField
              id="name"
              forceLabel="First Name"
              value={entity?.claim?.claimant?.firstName}
              schema={ReferralRequestSchema}
            />
            <ReadOnlyField
              id="name"
              forceLabel="Last Name"
              value={entity?.claim?.claimant?.lastName}
              schema={ReferralRequestSchema}
            />
            <ReadOnlyField
              id="gender_DisplayName"
              forceLabel="Gender"
              value={entity?.claim?.claimant?.gender?.displayName}
              schema={ReferralRequestSchema}
            />
            <ReadOnlyField
              id="dateOfBirth"
              forceLabel="Date Of Birth"
              value={
                entity.dateOfBirth
                  ? formatDate(parseRawDateString(entity.dateOfBirth) || new Date(), 'd')
                  : ''
              }
              schema={ReferralRequestSchema}
            />
            {showSection && (
              <Fragment>
                <TitleReadOnly>Claimant Address</TitleReadOnly>
                <ReadOnlyField
                  id="streetAddress"
                  forceLabel="Street Address"
                  value={entity?.claim?.claimant?.streetAddress}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="state_DisplayName"
                  forceLabel="State"
                  value={entity?.claim?.claimant?.state?.displayName}
                  schema={ReferralRequestSchema}
                />
                 <ReadOnlyField
                  id="city"
                  forceLabel="City"
                  value={entity?.claim?.claimant?.city}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="zipCode"
                  forceLabel="Zip Code"
                  value={entity?.claim?.claimant?.zipCode}
                  schema={ReferralRequestSchema}
                />
                <TitleReadOnly>Claimant Contact Details</TitleReadOnly>
                <ReadOnlyField
                  id="phoneNumber"
                  forceLabel="Phone Number"
                  value={
                    entity?.claim?.claimant?.phone ||
                    entity?.claim?.claimant?.mobilePhone ||
                    entity?.claim?.claimant?.phoneOther
                  }
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="email"
                  forceLabel="Email"
                  value={entity?.claim?.claimant?.email}
                  schema={ReferralRequestSchema}
                />
              </Fragment>
            )}
          </PageCardSection>
        </PageCardRow>
      </PageCard>
      <PageCard className={classes.root} backgroundIcon="lab_profile">
        <PageCardRow>
          <PageCardSection title="Claim Details">
            <ReadOnlyField
              id="claimNumber"
              forceLabel="Claim / Client Number"
              value={entity.claimNumber}
              schema={ReferralRequestSchema}
            />
            <ReadOnlyField
              id="referralNumber"
              forceLabel="Referral Number"
              value={entity.referralNumber}
              schema={ReferralRequestSchema}
            />
            <ReadOnlyField
              id="dateOfInjury"
              forceLabel="Date of Injury"
              value={
                entity.dateOfInjury
                  ? formatDate(parseRawDateString(entity.dateOfInjury) || new Date(), 'd')
                  : ''
              }
              schema={ReferralRequestSchema}
            />
            {showSection && (
              <Fragment>
                <ReadOnlyField
                  id="dateOfRequest"
                  forceLabel="Date and Time Request Received"
                  value={
                    entity.dateOfRequest
                      ? formatDate(parseUtcToLocal(entity.dateOfRequest) || new Date(), 'g')
                      : ''
                  }
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="dateOfExan"
                  forceLabel="Date of Exam"
                  value={
                    entity.dateOfExam
                      ? formatDate(parseRawDateString(entity.dateOfExam) || new Date(), 'd')
                      : ''
                  }
                  schema={ReferralRequestSchema}
                />
              </Fragment>
            )}
            {(showSection || userdata?.isClient) && (
              <Fragment>
                <TitleReadOnly>Employer Information</TitleReadOnly>
                <ReadOnlyField
                  id="EmployerName"
                  forceLabel="Employer Name"
                  value={entity?.claim?.employerName}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="employerAddress"
                  forceLabel="Employer Location"
                  value={entity?.claim?.employerLocation?.displayName}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="employerZipCode"
                  forceLabel="Employer ZIP Code"
                  value={entity?.claim?.employerZipCode}
                  schema={ReferralRequestSchema}
                />
                <TitleReadOnly>Medication Details</TitleReadOnly>
                {entity?.claim?.medications?.map(
                  (medication: any, index: number) => (
                    <div key={index}>
                      <ReadOnlyField
                        id="medications"
                        label="Medication Name"
                        value={medication?.medicationName ?? ''}
                        forceLabel="Medication Name"
                        schema={ReferralRequestSchema}
                      />
                      <ReadOnlyField
                        id="medications"
                        label="Medication Details"
                        value={medication?.medicationDetails ?? ''}
                        forceLabel="Medication Details"
                        schema={ReferralRequestSchema}
                      />
                    </div>
                  )
                )}
              </Fragment>
            )}
          </PageCardSection>
        </PageCardRow>
      </PageCard>
      <PageCard className={classes.root} backgroundIcon="gavel">
        <PageCardRow>
          <PageCardSection title="Attorney Details">
            {entity?.attorneys?.map((attorney: any, index: number) => (
              <div key={index}>
                <TitleReadOnly>Attorney {index + 1}</TitleReadOnly>
                <ReadOnlyField
                  id={`attorney1Name${index}`}
                  forceLabel="Name"
                  value={attorney?.attorneyName ?? ''}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id={`attorney1PC${index}`}
                  forceLabel="Primary Contact (Phone or Email)"
                  value={attorney?.primaryContact ?? ''}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id={`attorney1AC${index}`}
                  forceLabel="Additional Contact (Phone or Email)"
                  value={attorney?.additionalContact ?? ''}
                  schema={ReferralRequestSchema}
                />
              </div>
            ))}
          </PageCardSection>
        </PageCardRow>
      </PageCard>
      <PageCard className={classes.root} backgroundIcon="diagnosis">
        <PageCardRow>
          <PageCardSection title="Report Details">
            <Render
              condition={
                !(
                  userdata?.isNurse ||
                  userdata?.isDoctor ||
                  userdata?.isDoctorAdmin ||
                  userdata?.isReviewer
                )
              }
            >
              <TitleReadOnly>Date</TitleReadOnly>
              <ReadOnlyField
                id="dueDate"
                forceLabel="Due Date & Time"
                value={
                  entity.dueDate
                    ? formatDate(parseUtcToLocal(entity.dueDate) || new Date(), 'g')
                    : ''
                }
                schema={ReferralRequestSchema}
              />
              <ReadOnlyField
                id="tat"
                forceLabel="TAT (Turn Around Time)"
                value={entity.tat?.displayName}
                schema={ReferralRequestSchema}
              />
              <ReadOnlyField
                id="tATPriority"
                forceLabel="TAT Priority"
                value={entity.tatPriority?.displayName}
                schema={ReferralRequestSchema}
              />
            </Render>
            <TitleReadOnly>Requirements</TitleReadOnly>
            <ReadOnlyField
              id="jurisditionalMatchRequired"
              forceLabel="Jurisdictional Match Required"
              value={entity.jurisditionalMatchRequired ? 'Yes' : 'No'}
              schema={ReferralRequestSchema}
            />
            <ReadOnlyField
              id="benefitState"
              forceLabel="State of Jurisdiction"
              value={entity?.claim?.benefitState?.displayName}
              schema={ReferralRequestSchema}
            />
            <ReadOnlyField
              id="service"
              forceLabel="Service"
              value={entity.service?.name}
              schema={ReferralRequestSchema}
            />
            <ReadOnlyField
              id="subService"
              forceLabel="Sub-service"
              value={entity.subService?.name}
              schema={ReferralRequestSchema}
            />
            <ReadOnlyField
              id="numberOfReviews"
              forceLabel="Number of Reviews"
              value={entity.numberOfReviews}
              schema={ReferralRequestSchema}
            />
            {entity?.requestedReviewerSpecialties?.map(
              (specialty: any, index: number) => (
                <div key={index}>
                  <ReadOnlyField
                    id={`requestedReviewerSpecialties${index}`}
                    forceLabel={`Requested Reviewer Specialty ${index + 1}`}
                    value={specialty?.displayName ?? ''}
                    schema={ReferralRequestSchema}
                  />
                </div>
              )
            )}
            <TitleReadOnly>Attending Provider Contact</TitleReadOnly>
            <ReadOnlyField
              id="apContactPreference"
              forceLabel="AP Contact Preference"
              value={entity.apContactPreference?.displayName}
              schema={ReferralRequestSchema}
            />
            {entity?.attendingProviders?.map((provider: any, index: number) => (
              <div key={index}>
                <TitleReadOnly>
                  Attending Provider {index} Information
                </TitleReadOnly>
                <ReadOnlyField
                  id="attendingProviders"
                  label={`AP${index} Name`}
                  value={provider?.apName ?? ''}
                  forceLabel="Attending Provider Name"
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="attendingProviders"
                  label={`AP${index} Specialty`}
                  value={provider?.apSpecialty?.displayName ?? ''}
                  forceLabel="Attending Provider Specialty"
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="attendingProviders"
                  label={`AP${index} Phone Number`}
                  value={provider?.apPhoneNumber ?? ''}
                  forceLabel="Attending Provider Phone Number"
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="apEmail"
                  label={`AP${index} Fax or Email`}
                  value={provider?.apEmail ?? ''}
                  forceLabel="Attending Provider Fax or Email"
                  schema={ReferralRequestSchema}
                />
              </div>
            ))}
          </PageCardSection>
        </PageCardRow>
      </PageCard>
      <PageCard className={classes.root} backgroundIcon="contact_support">
        <PageCardRow>
          <PageCardSection title="Report Questions">
            {entity?.clientReportQuestions?.map((question: any, i: number) => (
              <div key={`clientReportQuestion${i}`} className="read-only-field">
                <span>{question?.question}</span>
                <div className="dashed-line"></div>
              </div>
            ))}
          </PageCardSection>
        </PageCardRow>
      </PageCard>
      <PageCard className={classes.root}>
        <PageCardRow>
          <PageCardSection title="Additional Information">
            <TitleReadOnly>Special Instructions</TitleReadOnly>
            <span style={{ whiteSpace: 'pre-wrap' }}>{entity.specialInstructions}</span>
          </PageCardSection>
        </PageCardRow>
      </PageCard>
    </div>
  );
};
